/**
 * Recently built or opened decks in localStorage, by deck seed. Lets a shared
 * link open instantly on this device, even after the card data has changed.
 */

import { computeDeckSeed, isDeckSeed } from "./deck-seed";
import type { DeckView } from "./types";

const PREFIX = "cde-deck:";
const INDEX_KEY = "cde-deck-index";
const MAX_DECKS = 12;
const DECK_STORAGE_EVENT = "cde-deck-storage-updated";

/** Old saved builds predate the category- and token-aware engine behavior. */
export function hasCurrentBuildMetadata(deck: DeckView | null | undefined): deck is DeckView {
  return (
    deck?.result.versions?.engine === "cde-engine-0.2.0" &&
    typeof deck.result.versions.tokens === "string" &&
    deck.result.versions.tokens !== ""
  );
}

function readIndex(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(INDEX_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter(isDeckSeed) : [];
  } catch {
    return [];
  }
}

function writeIndex(seeds: readonly string[]): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(seeds));
}

/** Saves the deck, evicting the oldest ones when full or over quota. */
export function saveDeckToLocalStorage(deck: DeckView): void {
  const seed = deck.result.deckSeed;
  if (!isDeckSeed(seed)) return;
  let seeds = [seed, ...readIndex().filter((s) => s !== seed)];
  const payload = JSON.stringify(deck);
  for (;;) {
    for (const evicted of seeds.slice(MAX_DECKS)) {
      localStorage.removeItem(PREFIX + evicted);
    }
    seeds = seeds.slice(0, MAX_DECKS);
    try {
      localStorage.setItem(PREFIX + seed, payload);
      writeIndex(seeds);
      window.dispatchEvent(new Event(DECK_STORAGE_EVENT));
      return;
    } catch {
      // Quota exceeded: drop the oldest other deck and retry.
      if (seeds.length <= 1) return;
      localStorage.removeItem(PREFIX + (seeds.pop() as string));
    }
  }
}

export function loadDeckFromLocalStorage(deckSeed: string): DeckView | null {
  if (!isDeckSeed(deckSeed)) return null;
  try {
    const deck = JSON.parse(localStorage.getItem(PREFIX + deckSeed) ?? "null") as DeckView | null;
    return deck !== null &&
      hasCurrentBuildMetadata(deck) &&
      computeDeckSeed(deck.result) === deckSeed
      ? deck
      : null;
  } catch {
    return null;
  }
}

/** Returns stored decks from newest to oldest, skipping stale or invalid entries. */
export function listRecentDecksFromLocalStorage(): DeckView[] {
  return readIndex()
    .map((seed) => loadDeckFromLocalStorage(seed))
    .filter((deck): deck is DeckView => deck !== null);
}

/** Subscribe to same-tab saves and cross-tab localStorage updates. */
export function subscribeToDeckStorageUpdates(onUpdate: () => void): () => void {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === INDEX_KEY || event.key.startsWith(PREFIX)) onUpdate();
  };
  window.addEventListener(DECK_STORAGE_EVENT, onUpdate);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(DECK_STORAGE_EVENT, onUpdate);
    window.removeEventListener("storage", handleStorage);
  };
}
